import analytics from "fond/analytics";
import { versionsSlice } from "fond/api";
import {
  ADD_IMPORT,
  addImportDispatch,
  COMPLETE_IMPORT,
  completeImportAction,
  completeImportDispatch,
  loadImportsDispatch,
  REMOVE_IMPORT,
  removeImportAction,
  removeImportDispatch,
  START_IMPORT,
  startImportAction,
  startImportDispatch,
  SUBMIT_IMPORT,
  submitImportAction,
  submitImportDispatch,
  UPDATE_IMPORT,
  updateImportAction,
  updateImportDispatch,
  VERSION_UPLOAD_CONCURRENCY_LIMIT,
} from "fond/redux/imports";
import { AppThunk, AppThunkDispatch, ImportStatus, Store } from "fond/types";

export const loadImports =
  ({ versionId }: loadImportsDispatch): AppThunk<Promise<void>> =>
  async (dispatch: AppThunkDispatch) => {
    const result = dispatch(versionsSlice.endpoints.getImportStatus.initiate({ versionId }, { forceRefetch: true }));
    const versionImportStatusResponse = await result;
    result.unsubscribe();

    for (const layerImport of versionImportStatusResponse?.data?.Layers || []) {
      if (![ImportStatus.COMPLETE, ImportStatus.ERROR].includes(layerImport.ImportStatus.Status)) {
        dispatch({
          type: ADD_IMPORT,
          versionId,
          layerKey: layerImport.LayerKey,
          layerId: layerImport.ID,
          displayName: layerImport.LayerKey, // TODO: Get the config label from the API response
          files: [],
          started: new Date(layerImport.ImportStatus.StartedAt),
          submitted: new Date(),
          status: layerImport.ImportStatus.Status,
          progress: 0,
        });
      }
    }
  };

export const addImport =
  ({ versionId, layerKey, layerId, displayName, files, started, submitted, status, progress }: addImportDispatch): AppThunk<Promise<void>> =>
  async (dispatch: AppThunkDispatch, getState: () => Store) => {
    // Read the current import state.
    const { imports } = getState();

    // eslint-disable-next-line no-async-promise-executor
    await new Promise<void>(async (resolve, reject) => {
      // Handle forbidden import attempts.
      const existingImport = imports?.[versionId]?.[layerKey];
      if (existingImport != null && ![ImportStatus.COMPLETE, ImportStatus.ERROR].includes(existingImport.status)) {
        return reject(Error("Please dismiss the currently active import and try again."));
      } else if (imports[versionId] == null && Object.keys(imports).length >= VERSION_UPLOAD_CONCURRENCY_LIMIT) {
        return reject(
          Error(`Only ${VERSION_UPLOAD_CONCURRENCY_LIMIT} versions may be imported at the same time. Please dismiss an import and try again.`)
        );
      }

      if (existingImport != null) {
        await dispatch({
          type: REMOVE_IMPORT,
          versionId,
          layerKey,
        });
      }

      // Dispatch the action and resolve.
      dispatch({
        type: ADD_IMPORT,
        versionId,
        layerKey,
        layerId,
        displayName,
        files,
        started: started === undefined ? null : started,
        submitted: submitted === undefined ? null : submitted,
        status: status === undefined ? ImportStatus.STARTING : status,
        progress: progress === undefined ? 0 : progress,
      });
      return resolve();
    });
  };

export const startImport = ({ versionId, layerKey, started }: startImportDispatch): startImportAction => ({
  type: START_IMPORT,
  versionId,
  layerKey,
  started,
});

export const submitImport = ({ versionId, layerKey, submitted }: submitImportDispatch): submitImportAction => ({
  type: SUBMIT_IMPORT,
  versionId,
  layerKey,
  submitted,
});

export const updateImport = ({ versionId, layerKey, layerId, status, progress, error, completed }: updateImportDispatch): updateImportAction => {
  if (error) {
    analytics.track("Failed layer import", { layerId, error, completed: completed ? completed.toISOString() : null });
  }
  return {
    type: UPDATE_IMPORT,
    versionId,
    layerKey,
    layerId,
    status,
    progress,
    error,
    completed,
  };
};

export const completeImport = ({ versionId, layerKey, completed }: completeImportDispatch): completeImportAction => ({
  type: COMPLETE_IMPORT,
  versionId,
  layerKey,
  completed,
});

export const removeImport = ({ versionId, layerKey }: removeImportDispatch): removeImportAction => ({
  type: REMOVE_IMPORT,
  versionId,
  layerKey,
});
